import React from 'react';

import i18n from 'util/i18n';

import { Summary, DateSummary } from 'components/Summary';

import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';

import { PSYCHOLOGICAL, PSYCHOLOGICAL_HOSPITALIZATIONS } from 'config/formSections/psychological';

import { EmptyListInstance } from 'forms/shared/models';
import Branch from 'components/Form/Branch';
import Accordion from 'components/Form/Accordion';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import { PVQ_A_B_D } from 'constants/formTypes';
import HospitalizationItem from './HospitalizationItem';

const sectionConfig = {
  key: PSYCHOLOGICAL_HOSPITALIZATIONS.key,
  section: PSYCHOLOGICAL.name,
  store: PSYCHOLOGICAL.store,
  subsection: PSYCHOLOGICAL_HOSPITALIZATIONS.name,
  storeKey: PSYCHOLOGICAL_HOSPITALIZATIONS.storeKey,
};

export class Hospitalizations extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.updateHospitalized = this.updateHospitalized.bind(this);
    this.updateList = this.updateList.bind(this);
    this.updateComments = this.updateComments.bind(this);
    this.updateSummaryComments = this.updateSummaryComments.bind(this);
    this.summary = this.summary.bind(this);
  }

  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      List: this.props.List,
      Hospitalized: this.props.Hospitalized,
      Comments: this.props.Comments,
      SummaryComments: this.props.SummaryComments,
      ...queue,
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateHospitalized = (values) => {
    this.update({
      Hospitalized: values,
      List: values.value === 'Yes' ? this.props.List : [],
    });
  };

  updateComments(values) {
    this.update({
      Comments: values,
    });
  }

  updateSummaryComments(values) {
    this.update({
      SummaryComments: values,
    });
  }

  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const treatmentDate = o.TreatmentDate || {};
    const date = DateSummary(treatmentDate);
    const facility = (o.Facility || {}).value || '';

    return Summary({
      type: i18n.t('psychological.hospitalization.collection.itemType'),
      index,
      left: facility,
      right: date,
      placeholder: i18n.t('psychological.hospitalization.collection.summary'),
    });
  };

  render() {
    const { errors } = this.props;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const sectionPrefix = (this.props.formType === PVQ_A_B_D) ? 'Part D:' : 'Section 28:';

    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div
        className="section-content hospitalizations"
        data-section={PSYCHOLOGICAL.key}
        data-subsection={PSYCHOLOGICAL_HOSPITALIZATIONS.key}
      >
        <h1 className="section-header">{`${sectionPrefix} ${i18n.t('psychological.pvq.destination.hospitalization')}`}</h1>
        <Branch
          name="hospitalized"
          label={i18n.t('psychological.pvq.heading.hospitalization')}
          labelSize="h4"
          {...this.props.Hospitalized}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateHospitalized} // eslint-disable-line @typescript-eslint/unbound-method
          scrollIntoView={this.props.scrollIntoView}
          attribute="Hospitalized"
        />
        {this.props.Hospitalized.value === 'Yes'
          && (
            <Accordion
              defaultState={this.props.defaultState}
              {...this.props.List}
              scrollToBottom={this.props.scrollToBottom}
              summary={this.summary} // eslint-disable-line @typescript-eslint/unbound-method
              onUpdate={this.updateList} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
              errors={accordionErrors}
              description={i18n.t('psychological.pvq.hospitalization.collection.description')}
              appendTitle={i18n.t('psychological.pvq.hospitalization.collection.appendTitle')}
              appendLabel={i18n.t('psychological.pvq.hospitalization.collection.appendLabel')}
              required={this.props.required}
              attribute="List"
              scrollIntoView={this.props.scrollIntoView}
            >
              <HospitalizationItem
                name="Item"
                attribute="List"
                bind
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                formType={this.props.formType}
              />
            </Accordion>
          )}
      </div>
    ) : (
      <div
        className="section-content hospitalizations"
        data-section={PSYCHOLOGICAL.key}
        data-subsection={PSYCHOLOGICAL_HOSPITALIZATIONS.key}
      >
        <h1 className="section-header">{i18n.t('psychological.destination.hospitalization')}</h1>
        <Branch
          name="hospitalized"
          label={i18n.t('psychological.heading.hospitalization')}
          labelSize="h4"
          {...this.props.Hospitalized}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateHospitalized} // eslint-disable-line @typescript-eslint/unbound-method
          scrollIntoView={this.props.scrollIntoView}
          attribute="Hospitalized"
        />
        <Comments
          title={i18n.t('psychological.hospitalization.comments')}
          name="Comments"
          className="optional-comments"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />

        {this.props.Hospitalized.value === 'Yes'
          && (
            <><Accordion
              defaultState={this.props.defaultState}
              {...this.props.List}
              scrollToBottom={this.props.scrollToBottom}
              summary={this.summary} // eslint-disable-line @typescript-eslint/unbound-method
              onUpdate={this.updateList} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
              errors={accordionErrors}
              description={i18n.t('psychological.hospitalization.collection.description')}
              appendTitle={i18n.t('psychological.hospitalization.collection.appendTitle')}
              appendLabel={i18n.t('psychological.hospitalization.collection.appendLabel')}
              required={this.props.required}
              attribute="List"
              scrollIntoView={this.props.scrollIntoView}
            >
              <HospitalizationItem
                name="Item"
                attribute="List"
                bind
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
              />
            </Accordion>
              <Comments
                title={i18n.t('psychological.hospitalization.summaryComments')}
                name="SummaryComments"
                className="optional-comments"
                attribute="SummaryComments"
                {...this.props.SummaryComments}
                onUpdate={this.updateSummaryComments}
                required={false}
              />
            </>
          )}
      </div>
    );
  }
}

Hospitalizations.defaultProps = {
  Hospitalized: {},
  Comments: {},
  SummaryComments: {},
  List: EmptyListInstance,
  defaultState: true,
  formType: '',
  onUpdate: () => { },
  onError: (value, arr) => arr,
  dispatch: () => { },
  scrollToBottom: '.bottom-btns',
  errors: [],
};

export default connectSubsection(Hospitalizations, sectionConfig);
